// 类型定义
interface PixelData {
    x: number;
    y: number;
    color: string;
}

interface Template {
    name: string;
    width: number;
    height: number;
    data: string;
}

// 应用状态
class PixelArtEditor {
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private gridWidth: number = 32;
    private gridHeight: number = 32;
    private pixelSize: number = 16;
    private currentColor: string = '#000000';
    private currentTool: string = 'pen';
    private showGrid: boolean = true;
    private isDrawing: boolean = false;
    private history: ImageData[] = [];
    private historyIndex: number = -1;
    private startX: number = 0;
    private startY: number = 0;
    private tempCanvas: HTMLCanvasElement;
    private tempCtx: CanvasRenderingContext2D;
    private currentPhoto: HTMLImageElement | null = null;
    private zoomLevel: number = 100;

    constructor() {
        this.canvas = document.getElementById('pixelCanvas') as HTMLCanvasElement;
        this.ctx = this.canvas.getContext('2d', { willReadFrequently: true })!;

        // 创建临时画布用于工具预览
        this.tempCanvas = document.createElement('canvas');
        this.tempCtx = this.tempCanvas.getContext('2d')!;

        this.initCanvas();
        this.initEventListeners();
        this.loadTemplates();
        this.updateCurrentColor();
        this.saveHistory();
    }

    private initCanvas(): void {
        this.updateCanvasDisplay();

        this.tempCanvas.width = this.gridWidth;
        this.tempCanvas.height = this.gridHeight;

        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, this.gridWidth, this.gridHeight);
        this.drawGrid();

        document.getElementById('canvasSizeInfo')!.textContent = `${this.gridWidth}×${this.gridHeight}`;
    }

    private updateCanvasDisplay(): void {
        this.canvas.width = this.gridWidth;
        this.canvas.height = this.gridHeight;

        // 根据缩放级别计算显示尺寸
        const baseSize = Math.min(600, window.innerWidth - 100);
        const scaledSize = (baseSize * this.zoomLevel) / 100;

        this.canvas.style.width = `${scaledSize}px`;
        this.canvas.style.height = `${scaledSize}px`;

        // 更新缩放显示
        document.getElementById('zoomDisplay')!.textContent = `${this.zoomLevel}%`;
        document.getElementById('zoomInfo')!.textContent = `${this.zoomLevel}%`;
    }

    private setZoom(level: number): void {
        this.zoomLevel = Math.max(50, Math.min(400, level));
        this.updateCanvasDisplay();

        const zoomSlider = document.getElementById('zoomSlider') as HTMLInputElement;
        zoomSlider.value = this.zoomLevel.toString();
    }

    private drawGrid(): void {
        if (!this.showGrid) return;

        this.ctx.strokeStyle = '#e0e0e0';
        this.ctx.lineWidth = 0.5;

        for (let x = 0; x <= this.gridWidth; x++) {
            this.ctx.beginPath();
            this.ctx.moveTo(x, 0);
            this.ctx.lineTo(x, this.gridHeight);
            this.ctx.stroke();
        }

        for (let y = 0; y <= this.gridHeight; y++) {
            this.ctx.beginPath();
            this.ctx.moveTo(0, y);
            this.ctx.lineTo(this.gridWidth, y);
            this.ctx.stroke();
        }
    }

    private getPixelCoordinates(e: MouseEvent): { x: number; y: number } {
        const rect = this.canvas.getBoundingClientRect();
        const scaleX = this.canvas.width / rect.width;
        const scaleY = this.canvas.height / rect.height;

        const x = Math.floor((e.clientX - rect.left) * scaleX);
        const y = Math.floor((e.clientY - rect.top) * scaleY);

        return { x, y };
    }

    private drawPixel(x: number, y: number, color: string): void {
        if (x < 0 || x >= this.gridWidth || y < 0 || y >= this.gridHeight) return;

        this.ctx.fillStyle = color;
        this.ctx.fillRect(x, y, 1, 1);
    }

    private erasePixel(x: number, y: number): void {
        this.drawPixel(x, y, '#ffffff');
    }

    private fill(startX: number, startY: number, targetColor: string): void {
        const imageData = this.ctx.getImageData(0, 0, this.gridWidth, this.gridHeight);
        const pixels = imageData.data;

        const startPos = (startY * this.gridWidth + startX) * 4;
        const startR = pixels[startPos];
        const startG = pixels[startPos + 1];
        const startB = pixels[startPos + 2];
        const startA = pixels[startPos + 3];

        const fillColor = this.hexToRgb(targetColor);
        if (!fillColor) return;

        if (startR === fillColor.r && startG === fillColor.g &&
            startB === fillColor.b && startA === 255) {
            return;
        }

        const stack: [number, number][] = [[startX, startY]];

        while (stack.length) {
            const [x, y] = stack.pop()!;
            if (x < 0 || x >= this.gridWidth || y < 0 || y >= this.gridHeight) continue;

            const pos = (y * this.gridWidth + x) * 4;
            const r = pixels[pos];
            const g = pixels[pos + 1];
            const b = pixels[pos + 2];
            const a = pixels[pos + 3];

            if (r === startR && g === startG && b === startB && a === startA) {
                pixels[pos] = fillColor.r;
                pixels[pos + 1] = fillColor.g;
                pixels[pos + 2] = fillColor.b;
                pixels[pos + 3] = 255;

                stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
            }
        }

        this.ctx.putImageData(imageData, 0, 0);
        this.redrawCanvas();
    }

    private eyeDropper(x: number, y: number): void {
        const imageData = this.ctx.getImageData(x, y, 1, 1);
        const [r, g, b] = imageData.data;
        this.currentColor = this.rgbToHex(r, g, b);
        this.updateCurrentColor();
    }

    private drawLine(x0: number, y0: number, x1: number, y1: number, color: string): void {
        const dx = Math.abs(x1 - x0);
        const dy = Math.abs(y1 - y0);
        const sx = x0 < x1 ? 1 : -1;
        const sy = y0 < y1 ? 1 : -1;
        let err = dx - dy;

        while (true) {
            this.drawPixel(x0, y0, color);

            if (x0 === x1 && y0 === y1) break;
            const e2 = 2 * err;
            if (e2 > -dy) {
                err -= dy;
                x0 += sx;
            }
            if (e2 < dx) {
                err += dx;
                y0 += sy;
            }
        }
    }

    private drawRectangle(x0: number, y0: number, x1: number, y1: number, color: string, fill: boolean = false): void {
        const minX = Math.min(x0, x1);
        const maxX = Math.max(x0, x1);
        const minY = Math.min(y0, y1);
        const maxY = Math.max(y0, y1);

        if (fill) {
            for (let x = minX; x <= maxX; x++) {
                for (let y = minY; y <= maxY; y++) {
                    this.drawPixel(x, y, color);
                }
            }
        } else {
            for (let x = minX; x <= maxX; x++) {
                this.drawPixel(x, minY, color);
                this.drawPixel(x, maxY, color);
            }
            for (let y = minY; y <= maxY; y++) {
                this.drawPixel(minX, y, color);
                this.drawPixel(maxX, y, color);
            }
        }
    }

    private hexToRgb(hex: string): { r: number; g: number; b: number } | null {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
        } : null;
    }

    private rgbToHex(r: number, g: number, b: number): string {
        return '#' + [r, g, b].map(x => {
            const hex = x.toString(16);
            return hex.length === 1 ? '0' + hex : hex;
        }).join('');
    }

    private saveHistory(): void {
        const imageData = this.ctx.getImageData(0, 0, this.gridWidth, this.gridHeight);
        this.history = this.history.slice(0, this.historyIndex + 1);
        this.history.push(imageData);
        this.historyIndex++;

        if (this.history.length > 50) {
            this.history.shift();
            this.historyIndex--;
        }
    }

    private undo(): void {
        if (this.historyIndex > 0) {
            this.historyIndex--;
            this.ctx.putImageData(this.history[this.historyIndex], 0, 0);
            this.redrawCanvas();
        }
    }

    private redo(): void {
        if (this.historyIndex < this.history.length - 1) {
            this.historyIndex++;
            this.ctx.putImageData(this.history[this.historyIndex], 0, 0);
            this.redrawCanvas();
        }
    }

    private redrawCanvas(): void {
        const imageData = this.ctx.getImageData(0, 0, this.gridWidth, this.gridHeight);
        this.ctx.putImageData(imageData, 0, 0);
        this.drawGrid();
    }

    private updateCurrentColor(): void {
        const display = document.getElementById('currentColorDisplay') as HTMLDivElement;
        const picker = document.getElementById('colorPicker') as HTMLInputElement;
        display.style.background = this.currentColor;
        picker.value = this.currentColor;
    }

    private exportImage(): void {
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = this.gridWidth;
        exportCanvas.height = this.gridHeight;
        const exportCtx = exportCanvas.getContext('2d')!;

        const imageData = this.ctx.getImageData(0, 0, this.gridWidth, this.gridHeight);
        exportCtx.putImageData(imageData, 0, 0);

        exportCanvas.toBlob((blob) => {
            if (blob) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `pixel-art-${Date.now()}.png`;
                a.click();
                URL.revokeObjectURL(url);
            }
        }, 'image/png');
    }

    private resizeCanvas(width: number, height: number): void {
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = this.gridWidth;
        tempCanvas.height = this.gridHeight;
        const tempCtx = tempCanvas.getContext('2d')!;
        tempCtx.drawImage(this.canvas, 0, 0);

        this.gridWidth = width;
        this.gridHeight = height;
        this.tempCanvas.width = width;
        this.tempCanvas.height = height;

        this.updateCanvasDisplay();

        this.ctx.fillStyle = '#ffffff';
        this.ctx.fillRect(0, 0, width, height);
        this.ctx.drawImage(tempCanvas, 0, 0);
        this.redrawCanvas();

        document.getElementById('canvasSizeInfo')!.textContent = `${width}×${height}`;
    }

    private loadTemplates(): void {
        const templates: Template[] = [
            { name: '笑脸', width: 16, height: 16, data: this.generateSmileyTemplate() },
            { name: '心形', width: 16, height: 16, data: this.generateHeartTemplate() },
            { name: '星星', width: 16, height: 16, data: this.generateStarTemplate() },
            { name: '蘑菇', width: 16, height: 16, data: this.generateMushroomTemplate() }
        ];

        const templateGrid = document.querySelector('.template-grid')!;
        templates.forEach(template => {
            const item = document.createElement('div');
            item.className = 'template-item';

            const canvas = document.createElement('canvas');
            canvas.width = template.width;
            canvas.height = template.height;
            const ctx = canvas.getContext('2d')!;

            const img = new Image();
            img.onload = () => {
                ctx.drawImage(img, 0, 0);
            };
            img.src = template.data;

            const label = document.createElement('span');
            label.textContent = template.name;

            item.appendChild(canvas);
            item.appendChild(label);
            templateGrid.appendChild(item);

            item.addEventListener('click', () => {
                this.loadTemplate(template);
                this.closeModals();
            });
        });
    }

    private generateSmileyTemplate(): string {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#FFD93D';
        ctx.fillRect(0, 0, 16, 16);
        ctx.fillStyle = '#000000';
        ctx.fillRect(4, 5, 2, 2);
        ctx.fillRect(10, 5, 2, 2);
        ctx.fillRect(4, 10, 8, 2);

        return canvas.toDataURL();
    }

    private generateHeartTemplate(): string {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#FF6B6B';
        const pixels = [
            [0,0,0,1,1,1,0,0,0,1,1,1,0,0,0,0],
            [0,0,1,1,1,1,1,0,1,1,1,1,1,0,0,0],
            [0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
            [0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
            [0,1,1,1,1,1,1,1,1,1,1,1,1,1,0,0],
            [0,0,1,1,1,1,1,1,1,1,1,1,1,0,0,0],
            [0,0,0,1,1,1,1,1,1,1,1,1,0,0,0,0],
            [0,0,0,0,1,1,1,1,1,1,1,0,0,0,0,0],
            [0,0,0,0,0,1,1,1,1,1,0,0,0,0,0,0],
            [0,0,0,0,0,0,1,1,1,0,0,0,0,0,0,0]
        ];

        pixels.forEach((row, y) => {
            row.forEach((val, x) => {
                if (val) ctx.fillRect(x, y + 3, 1, 1);
            });
        });

        return canvas.toDataURL();
    }

    private generateStarTemplate(): string {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#FFE66D';
        ctx.fillRect(7, 2, 2, 2);
        ctx.fillRect(6, 4, 4, 2);
        ctx.fillRect(4, 6, 8, 2);
        ctx.fillRect(2, 8, 12, 2);
        ctx.fillRect(4, 10, 8, 2);
        ctx.fillRect(5, 12, 6, 2);

        return canvas.toDataURL();
    }

    private generateMushroomTemplate(): string {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#FF6B6B';
        ctx.fillRect(4, 4, 8, 6);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(6, 6, 2, 2);
        ctx.fillRect(9, 7, 1, 1);
        ctx.fillStyle = '#F5F5F5';
        ctx.fillRect(6, 10, 4, 4);

        return canvas.toDataURL();
    }

    private loadTemplate(template: Template): void {
        this.resizeCanvas(template.width, template.height);
        const img = new Image();
        img.onload = () => {
            this.ctx.drawImage(img, 0, 0);
            this.redrawCanvas();
            this.saveHistory();
        };
        img.src = template.data;
    }

    private closeModals(): void {
        document.querySelectorAll('.modal').forEach(modal => {
            modal.classList.remove('active');
        });
    }

    private initEventListeners(): void {
        // Canvas 事件
        this.canvas.addEventListener('mousedown', (e) => {
            this.isDrawing = true;
            const { x, y } = this.getPixelCoordinates(e);
            this.startX = x;
            this.startY = y;

            if (this.currentTool === 'pen') {
                this.drawPixel(x, y, this.currentColor);
                this.saveHistory();
            } else if (this.currentTool === 'eraser') {
                this.erasePixel(x, y);
                this.saveHistory();
            } else if (this.currentTool === 'fill') {
                this.fill(x, y, this.currentColor);
                this.saveHistory();
            } else if (this.currentTool === 'eyedropper') {
                this.eyeDropper(x, y);
            }
        });

        this.canvas.addEventListener('mousemove', (e) => {
            if (!this.isDrawing) return;

            const { x, y } = this.getPixelCoordinates(e);

            if (this.currentTool === 'pen') {
                this.drawPixel(x, y, this.currentColor);
            } else if (this.currentTool === 'eraser') {
                this.erasePixel(x, y);
            } else if (this.currentTool === 'line' || this.currentTool === 'rectangle') {
                this.ctx.putImageData(this.history[this.historyIndex], 0, 0);

                if (this.currentTool === 'line') {
                    this.drawLine(this.startX, this.startY, x, y, this.currentColor);
                } else {
                    this.drawRectangle(this.startX, this.startY, x, y, this.currentColor);
                }

                this.drawGrid();
            }
        });

        this.canvas.addEventListener('mouseup', () => {
            if (this.isDrawing && (this.currentTool === 'pen' || this.currentTool === 'eraser' ||
                this.currentTool === 'line' || this.currentTool === 'rectangle')) {
                this.saveHistory();
            }
            this.isDrawing = false;
        });

        // 工具按钮
        document.querySelectorAll('.tool-icon').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.tool-icon').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                this.currentTool = btn.getAttribute('data-tool')!;
            });
        });

        // 预设尺寸按钮
        document.querySelectorAll('.size-preset-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                document.querySelectorAll('.size-preset-btn').forEach(b => b.classList.remove('active'));
                btn.classList.add('active');

                const width = parseInt(btn.getAttribute('data-width')!);
                const height = parseInt(btn.getAttribute('data-height')!);
                this.resizeCanvas(width, height);
                this.saveHistory();

                // 更新自定义输入框
                (document.getElementById('canvasWidth') as HTMLInputElement).value = width.toString();
                (document.getElementById('canvasHeight') as HTMLInputElement).value = height.toString();
            });
        });

        // 面板标签切换
        document.querySelectorAll('.panel-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const panel = tab.getAttribute('data-panel')!;

                // 切换标签激活状态
                document.querySelectorAll('.panel-tab').forEach(t => t.classList.remove('active'));
                tab.classList.add('active');

                // 切换面板内容显示
                document.querySelectorAll('.panel-content').forEach(p => p.classList.remove('active'));
                document.querySelector(`[data-panel-content="${panel}"]`)?.classList.add('active');
            });
        });

        // 颜色选择
        const colorPicker = document.getElementById('colorPicker') as HTMLInputElement;
        colorPicker.addEventListener('input', (e) => {
            this.currentColor = (e.target as HTMLInputElement).value;
            this.updateCurrentColor();
        });

        const currentColorDisplay = document.getElementById('currentColorDisplay') as HTMLDivElement;
        currentColorDisplay.addEventListener('click', () => {
            colorPicker.click();
        });

        document.querySelectorAll('.palette-color').forEach(color => {
            color.addEventListener('click', () => {
                this.currentColor = color.getAttribute('data-color')!;
                this.updateCurrentColor();
            });
        });

        // 网格切换
        const gridToggle = document.getElementById('gridToggle') as HTMLInputElement;
        gridToggle.addEventListener('change', () => {
            this.showGrid = gridToggle.checked;
            this.redrawCanvas();
        });

        // 缩放控制
        const zoomSlider = document.getElementById('zoomSlider') as HTMLInputElement;
        const zoomInBtn = document.getElementById('zoomInBtn')!;
        const zoomOutBtn = document.getElementById('zoomOutBtn')!;

        zoomSlider.addEventListener('input', () => {
            this.setZoom(parseInt(zoomSlider.value));
        });

        zoomInBtn.addEventListener('click', () => {
            this.setZoom(this.zoomLevel + 10);
        });

        zoomOutBtn.addEventListener('click', () => {
            this.setZoom(this.zoomLevel - 10);
        });

        // 画布调整
        const resizeBtn = document.getElementById('resizeBtn')!;
        resizeBtn.addEventListener('click', () => {
            const width = parseInt((document.getElementById('canvasWidth') as HTMLInputElement).value);
            const height = parseInt((document.getElementById('canvasHeight') as HTMLInputElement).value);
            this.resizeCanvas(width, height);
            this.saveHistory();

            // 取消预设按钮的选中状态
            document.querySelectorAll('.size-preset-btn').forEach(b => b.classList.remove('active'));
        });

        // 导出图片
        document.getElementById('exportBtn')!.addEventListener('click', () => this.exportImage());

        // 新建
        document.getElementById('newBtn')!.addEventListener('click', () => {
            if (confirm('确定要新建画布吗？未保存的内容将丢失。')) {
                this.ctx.fillStyle = '#ffffff';
                this.ctx.fillRect(0, 0, this.gridWidth, this.gridHeight);
                this.redrawCanvas();
                this.saveHistory();
            }
        });

        // 模板库
        const templateModal = document.getElementById('templateModal')!;
        document.getElementById('templateBtn')!.addEventListener('click', () => {
            templateModal.classList.add('active');
        });

        // 照片转像素
        const photoConverterModal = document.getElementById('photoConverterModal')!;
        const photoInput = document.getElementById('photoInput') as HTMLInputElement;
        const uploadArea = document.getElementById('uploadArea')!;

        document.getElementById('photoConverterBtn')!.addEventListener('click', () => {
            photoConverterModal.classList.add('active');
        });

        uploadArea.addEventListener('click', () => photoInput.click());

        uploadArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadArea.style.borderColor = 'var(--primary-color)';
        });

        uploadArea.addEventListener('dragleave', () => {
            uploadArea.style.borderColor = 'var(--border-color)';
        });

        uploadArea.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadArea.style.borderColor = 'var(--border-color)';
            const file = e.dataTransfer?.files[0];
            if (file) this.handlePhotoUpload(file);
        });

        photoInput.addEventListener('change', (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) this.handlePhotoUpload(file);
        });

        const pixelationLevel = document.getElementById('pixelationLevel') as HTMLInputElement;
        const pixelationValue = document.getElementById('pixelationValue')!;
        pixelationLevel.addEventListener('input', () => {
            pixelationValue.textContent = pixelationLevel.value;
            this.updatePhotoPreview(parseInt(pixelationLevel.value));
        });

        document.getElementById('applyConversionBtn')!.addEventListener('click', () => {
            this.applyPhotoConversion(parseInt(pixelationLevel.value));
            this.closeModals();
        });

        // 关闭模态框
        document.querySelectorAll('.modal-close').forEach(btn => {
            btn.addEventListener('click', () => this.closeModals());
        });

        document.querySelectorAll('.modal').forEach(modal => {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) this.closeModals();
            });
        });

        // 键盘快捷键
        document.addEventListener('keydown', (e) => {
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z') {
                    e.preventDefault();
                    this.undo();
                } else if (e.key === 'y') {
                    e.preventDefault();
                    this.redo();
                }
            }
        });
    }

    private handlePhotoUpload(file: File): void {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                this.currentPhoto = img;
                const previewImage = document.getElementById('previewImage') as HTMLImageElement;
                previewImage.src = img.src;
                document.getElementById('uploadArea')!.style.display = 'none';
                document.getElementById('converterPreview')!.style.display = 'flex';
                this.updatePhotoPreview(32);
            };
            img.src = e.target?.result as string;
        };
        reader.readAsDataURL(file);
    }

    private updatePhotoPreview(level: number): void {
        if (!this.currentPhoto) return;

        const canvas = document.createElement('canvas');
        canvas.width = level;
        canvas.height = level;
        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, level, level);

        const imgRatio = this.currentPhoto.width / this.currentPhoto.height;
        let drawWidth = level;
        let drawHeight = level;
        let offsetX = 0;
        let offsetY = 0;

        if (imgRatio > 1) {
            drawHeight = level / imgRatio;
            offsetY = (level - drawHeight) / 2;
        } else {
            drawWidth = level * imgRatio;
            offsetX = (level - drawWidth) / 2;
        }

        ctx.drawImage(this.currentPhoto, offsetX, offsetY, drawWidth, drawHeight);

        const previewImage = document.getElementById('previewImage') as HTMLImageElement;
        previewImage.src = canvas.toDataURL();
        previewImage.style.imageRendering = 'pixelated';
    }

    private applyPhotoConversion(level: number): void {
        if (!this.currentPhoto) return;

        const imgRatio = this.currentPhoto.width / this.currentPhoto.height;
        let canvasWidth = level;
        let canvasHeight = level;

        if (imgRatio > 1) {
            canvasHeight = Math.round(level / imgRatio);
        } else if (imgRatio < 1) {
            canvasWidth = Math.round(level * imgRatio);
        }

        this.resizeCanvas(canvasWidth, canvasHeight);

        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = canvasWidth;
        tempCanvas.height = canvasHeight;
        const tempCtx = tempCanvas.getContext('2d')!;

        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvasWidth, canvasHeight);

        tempCtx.drawImage(this.currentPhoto, 0, 0, canvasWidth, canvasHeight);

        this.ctx.drawImage(tempCanvas, 0, 0);
        this.redrawCanvas();
        this.saveHistory();

        this.currentPhoto = null;
        document.getElementById('uploadArea')!.style.display = 'flex';
        document.getElementById('converterPreview')!.style.display = 'none';
    }
}

// 初始化应用
new PixelArtEditor();
